import { CPLAT_API_ROUTES, fetcher, usePost } from '@/api';
import { MembershipInquiryPreviewTutorialProvider } from '@/components/membership/MembershipInquiryPreviewTutorial';
import { useMembershipPreviewMode } from '@/components/membership/MembershipContentLock';
import { useAppSelector } from '@/redux/store';
import type {
  InquiryCommentTemplate,
  InquiryCommentTemplateParams,
  InquiryCommentTemplateResponse, // 추가: /get_inquiry_list API 타입
  InquiryItem,
} from '@/types/api';
import { createContext, ReactNode, useContext, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { type MarketStoreListItem, SELECT_ALL_MARKET } from '../../_consts/constants';
import { type TabKey, DEFAULT_TAB } from '../../_consts/inquiryConstants';

import { useQuery, useSuspenseQuery } from '@tanstack/react-query';

import { getMarketKey } from '../../_utils/getMarketKey';
import {
  type MembershipInquiryPreviewResponse,
  createMembershipInquiryPreviewResponse,
  membershipInquiryPreviewTemplateResponse,
  membershipReviewInquiryPreviewDates,
} from '../membership-review-inquiry-mock';

interface InquiryContextType {
  // 유저가 갖고 있는 마켓 리스트
  marketList: MarketStoreListItem[];
  validMarkets: MarketStoreListItem[];
  selectedMarket: MarketStoreListItem | null;
  setSelectedMarket: (market: MarketStoreListItem) => void;
  currentTab: TabKey;
  setCurrentTab: (tab: TabKey) => void;
  selectedMarketId: string | null;
  setSelectedMarketId: (marketId: string | null) => void;
  startDate: Date | undefined;
  endDate: Date | undefined;
  handleStartDate: (date: Date | undefined) => void;
  handleEndDate: (date: Date | undefined) => void;
  filterSelect: { label: string; value: string };
  setFilterSelect: React.Dispatch<React.SetStateAction<{ label: string; value: string }>>;
  refetchInquiryList: () => void;
  // 문의 원본 데이터
  inquiryList: InquiryItem[];
  // 마켓별 문의 dictionary (key: getMarketKey)
  inquiryByMarketKey: Record<string, InquiryItem[]>;

  isLoading: boolean;
  isError: boolean;

  // 템플릿 관련
  templateList: InquiryCommentTemplate[];
  refetchTemplateList: () => void;
  handleAddOrEditTemplate: (params: InquiryCommentTemplateParams, onSuccess?: () => void) => void;
  handleDeleteTemplate: (params: InquiryCommentTemplateParams, onSuccess?: () => void) => void;
  isTemplateSaving: boolean;
  isTemplateDeleting: boolean;
}

const InquiryContext = createContext<InquiryContextType | undefined>(undefined);

export const InquiryProvider = ({ children }: { children: ReactNode }) => {
  const user = useAppSelector((state) => state.user);
  const isPreviewMode = useMembershipPreviewMode();
  const [searchParams, setSearchParams] = useSearchParams();
  const tabFromUrl = (searchParams.get('ctab') as TabKey) || DEFAULT_TAB;
  const selectedMarketIdFromUrl = searchParams.get('select') || null;

  const [currentTab, setCurrentTab] = useState<TabKey>(tabFromUrl);
  const [selectedMarketId, setSelectedMarketId] = useState<string | null>(selectedMarketIdFromUrl);

  // 날짜 상태
  const [startDate, setStartDate] = useState<Date | undefined>(() => {
    if (isPreviewMode) return membershipReviewInquiryPreviewDates.startDate;

    const today = new Date();
    const sevenDaysAgo = new Date(today);
    sevenDaysAgo.setDate(today.getDate() - 6);
    return sevenDaysAgo;
  });
  const [endDate, setEndDate] = useState<Date | undefined>(() =>
    isPreviewMode ? membershipReviewInquiryPreviewDates.endDate : new Date(),
  );

  // 날짜 핸들러
  const handleStartDate = (date: Date | undefined) => {
    if (date && endDate && date > endDate) {
      setStartDate(date);
      setEndDate(date);
    } else {
      setStartDate(date);
    }
  };
  const handleEndDate = (date: Date | undefined) => {
    if (date && startDate && date < startDate) {
      setStartDate(date);
      setEndDate(date);
    } else {
      setEndDate(date);
    }
  };

  const [filterSelect, setFilterSelect] = useState<{ label: string; value: string }>({
    label: isPreviewMode ? '오래된순' : '최신순',
    value: isPreviewMode ? 'oldest' : 'latest',
  });

  function safeDateParam(date: Date | undefined, fallback: Date): string {
    const useDate = date || fallback;
    const pad = (n: number) => n.toString().padStart(2, '0');
    return useDate.getFullYear() + '-' + pad(useDate.getMonth() + 1) + '-' + pad(useDate.getDate());
  }

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const sevenDaysAgo = new Date(today);
  sevenDaysAgo.setDate(today.getDate() - 6);

  const dateFrom = safeDateParam(startDate, sevenDaysAgo); // 없으면 7일전
  const dateTo = safeDateParam(endDate, today); // 없으면 오늘

  const params = {
    cplatToken: user.CplatToken,
    dateFrom,
    dateTo,
  };

  const { data, isLoading, isError, refetch } = useQuery<MembershipInquiryPreviewResponse>({
    queryKey: [
      isPreviewMode ? 'membershipInquiryPreview' : CPLAT_API_ROUTES.getInquiryList,
      params,
    ],
    queryFn: () => {
      if (isPreviewMode) return createMembershipInquiryPreviewResponse();

      return fetcher<MembershipInquiryPreviewResponse>({
        queryKey: [CPLAT_API_ROUTES.getInquiryList, params],
        method: 'POST',
      });
    },
    enabled: isPreviewMode || !!user.CplatToken,
  });

  // StoreList / InquiryList 분리
  const rawMarketList = useMemo(() => data?.data?.[0]?.StoreList ?? [], [data]);
  const inquiryList = useMemo(() => data?.data?.[0]?.InquiryList ?? [], [data]);

  // [전체] 선택 추가
  const marketList = useMemo(() => {
    if (!rawMarketList.length) return [];
    return [SELECT_ALL_MARKET, ...rawMarketList];
  }, [rawMarketList]);
  // 유효한 마켓 (전체 제외)
  const validMarkets = useMemo(() => marketList.filter((m) => m.MarketId !== 0), [marketList]);

  // 실제 선택된 마켓 객체
  const selectedMarket = useMemo(() => {
    if (!selectedMarketId) return marketList[0] ?? null;
    return (
      marketList.find(
        (m) => getMarketKey({ marketId: m.MarketId, storeId: m.StoreId }) === selectedMarketId,
      ) ??
      marketList[0] ??
      null
    );
  }, [marketList, selectedMarketId]);
  const setSelectedMarket = (market: MarketStoreListItem) => {
    setSelectedMarketId(getMarketKey({ marketId: market.MarketId, storeId: market.StoreId }));
  };

  // 문의를 마켓별로 bucket화 (object)
  const inquiryByMarketKey = useMemo(() => {
    return inquiryList.reduce<Record<string, InquiryItem[]>>((acc, item) => {
      const key = getMarketKey({ marketId: item.MarketId, storeId: item.StoreId });
      if (!acc[key]) acc[key] = [];
      acc[key].push(item);
      return acc;
    }, {});
  }, [inquiryList]);

  // ------- 템플릿 관련은 그대로 유지 -------
  const [templateList, setTemplateList] = useState<InquiryCommentTemplate[]>([]);

  const { data: templateData, refetch: refetchTemplateList } =
    useSuspenseQuery<InquiryCommentTemplateResponse>({
      queryKey: [
        isPreviewMode
          ? 'membershipInquiryPreviewTemplate'
          : CPLAT_API_ROUTES.getInquiryCommentTemplate,
        { cplatToken: user.CplatToken },
      ],
      queryFn: () => {
        if (isPreviewMode) return membershipInquiryPreviewTemplateResponse;

        return fetcher<InquiryCommentTemplateResponse>({
          queryKey: [CPLAT_API_ROUTES.getInquiryCommentTemplate, { cplatToken: user.CplatToken }],
          method: 'POST',
        });
      },
    });

  useEffect(() => {
    if (templateData?.data?.[0]?.InquiryCommentTemplateList) {
      setTemplateList(templateData.data[0].InquiryCommentTemplateList);
    } else {
      setTemplateList([]);
    }
  }, [templateData]);

  const { mutate: addOrEditTemplate, isPending: isTemplateSaving } = usePost(
    CPLAT_API_ROUTES.setInquiryCommentTemplate,
  );
  const handleAddOrEditTemplate = (
    params: InquiryCommentTemplateParams,
    onSuccess?: () => void,
  ) => {
    if (isPreviewMode) {
      onSuccess?.();
      return;
    }

    addOrEditTemplate(
      {
        ...params,
        cplatToken: user.CplatToken,
      },
      {
        onSuccess: () => {
          refetchTemplateList();
          onSuccess?.();
        },
      },
    );
  };

  const { mutate: deleteTemplate, isPending: isTemplateDeleting } = usePost(
    CPLAT_API_ROUTES.deleteInquiryCommentTemplate,
  );
  const handleDeleteTemplate = (params: InquiryCommentTemplateParams, onSuccess?: () => void) => {
    if (isPreviewMode) {
      onSuccess?.();
      return;
    }

    deleteTemplate(
      {
        ...params,
        cplatToken: user.CplatToken,
      },
      {
        onSuccess: () => {
          refetchTemplateList();
          onSuccess?.();
        },
      },
    );
  };

  // 쿼리와 상태 동기화
  useEffect(() => {
    setSearchParams(
      {
        ctab: currentTab,
        select: selectedMarket
          ? getMarketKey({ marketId: selectedMarket.MarketId, storeId: selectedMarket.StoreId })
          : '',
      },
      { replace: true },
    );
    // eslint-disable-next-line
  }, [currentTab, selectedMarket]);

  // 쿼리 변경 시 상태도 동기화
  useEffect(() => {
    if (tabFromUrl !== currentTab) setCurrentTab(tabFromUrl);
    if (selectedMarketIdFromUrl !== selectedMarketId) setSelectedMarketId(selectedMarketIdFromUrl);
    // eslint-disable-next-line
  }, [tabFromUrl, selectedMarketIdFromUrl]);

  const value: InquiryContextType = {
    marketList,
    validMarkets,
    selectedMarket,
    setSelectedMarket,
    refetchInquiryList: refetch,

    currentTab,
    setCurrentTab,
    selectedMarketId,
    setSelectedMarketId,
    startDate,
    endDate,
    handleStartDate,
    handleEndDate,
    filterSelect,
    setFilterSelect,
    inquiryList,
    inquiryByMarketKey,
    isLoading: isPreviewMode ? false : isLoading,
    isError: isPreviewMode ? false : isError,
    templateList,
    refetchTemplateList,
    handleAddOrEditTemplate,
    handleDeleteTemplate,
    isTemplateSaving,
    isTemplateDeleting,
  };
  return (
    <MembershipInquiryPreviewTutorialProvider>
      <InquiryContext.Provider value={value}>{children}</InquiryContext.Provider>
    </MembershipInquiryPreviewTutorialProvider>
  );
};

export const useInquiry = () => {
  const context = useContext(InquiryContext);
  if (!context) {
    throw new Error('useInquiry must be used within an InquiryProvider');
  }
  return context;
};
